import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { PublicLayout } from "@/components/layout/PublicLayout";
import { SEO } from "@/components/SEO";
import {
  ArrowRight,
  Brain,
  CheckCircle2,
  FileCheck2,
  FileText,
  GitBranch,
  Layers3,
  Lock,
  SearchCheck,
  Shield,
  Sparkles,
  UploadCloud,
  Zap,
} from "lucide-react";
import {
  HeroSection,
  BeforeAfterDemo,
  UseCaseCards,
  FAQSection,
  ScrollAnimatedSection,
} from "@/components/landing";

const workflowSteps = [
  {
    step: "01",
    title: "Capture context",
    desc: "Store product memory, project rules, constraints, and decisions so runs do not start from a blank prompt.",
    icon: Layers3,
  },
  {
    step: "02",
    title: "Generate or review",
    desc: "Start from a rough idea, feature brief, user story, or existing PRD draft.",
    icon: FileText,
  },
  {
    step: "03",
    title: "Evaluate readiness",
    desc: "Find ambiguity, missing decisions, weak acceptance criteria, and engineering questions.",
    icon: SearchCheck,
  },
  {
    step: "04",
    title: "Refine and export",
    desc: "Create new versions, preserve traceability, and export into Markdown, JSON, Jira, Linear, or Notion-ready formats.",
    icon: UploadCloud,
  },
];

const featureCards = [
  {
    icon: Layers3,
    title: "Product memory",
    description: "Keep durable product facts, workflows, constraints, edge cases, and decisions available for future runs.",
  },
  {
    icon: FileCheck2,
    title: "PRD review",
    description: "Bring an existing draft and identify what is unclear, missing, risky, or not yet testable.",
  },
  {
    icon: SearchCheck,
    title: "Readiness evaluation",
    description: "Score requirement quality and surface engineering questions before the handoff becomes expensive.",
  },
  {
    icon: GitBranch,
    title: "Versioned refinement",
    description: "Improve requirements through natural-language prompts while preserving previous versions and change history.",
  },
  {
    icon: UploadCloud,
    title: "Export-ready output",
    description: "Move requirements into Markdown, JSON, CSV, Jira, Linear, and Notion-ready formats.",
  },
  {
    icon: Shield,
    title: "Privacy-aware workflows",
    description: "Use standard or temporary processing modes for sensitive product work as the platform matures.",
  },
];

const securityFeatures = [
  "No model training on customer content",
  "Temporary processing mode for sensitive work",
  "Product and project context are visible before runs start",
  "Versioned history for review, rollback, and auditability",
];

export default function Landing() {
  return (
    <PublicLayout>
      <SEO
        canonical="/"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: "Reqqy",
          applicationCategory: "BusinessApplication",
          description:
            "Reqqy is a requirements intelligence platform that helps product teams generate, review, refine, and export clearer requirements before engineering work starts.",
          url: "https://reqqy.app",
          operatingSystem: "Web",
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
        }}
      />

      <HeroSection />

      <section id="workflow" className="relative scroll-mt-24 overflow-hidden py-14 md:py-20">
        <div className="absolute inset-0 bg-card/30" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border/40 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border/40 to-transparent" />
        <div className="absolute top-[-10%] left-[30%] h-[300px] w-[400px] rounded-full bg-primary/[0.04] blur-[120px]" />

        <div className="container relative z-10 px-4 md:px-6">
          <ScrollAnimatedSection>
            <div className="mx-auto mb-10 max-w-2xl text-center">
              <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                <Zap className="h-3.5 w-3.5" />
                Workflow
              </div>
              <h2 className="mb-3 font-sans text-2xl font-bold md:text-3xl">From scattered input to clearer handoff</h2>
              <p className="text-base text-muted-foreground">
                Reqqy supports both early-stage idea generation and existing PRD review, with context and traceability built into the process.
              </p>
            </div>

            <div className="grid gap-4 md:grid-cols-4">
              {workflowSteps.map((item, index) => (
                <div key={item.step} className="group relative">
                  <div className="relative h-full rounded-xl border border-border/30 glass p-5 transition-all duration-300 hover:-translate-y-0.5 hover:border-primary/20">
                    <div className="absolute inset-0 rounded-xl bg-gradient-to-b from-primary/[0.02] to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                    <div className="relative z-10">
                      <div className="mb-4 flex items-center justify-between">
                        <div className="font-sans text-3xl font-bold text-primary/20 transition-colors group-hover:text-primary/40">
                          {item.step}
                        </div>
                        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10">
                          <item.icon className="h-4 w-4 text-primary" />
                        </div>
                      </div>
                      <h3 className="mb-1.5 text-sm font-semibold">{item.title}</h3>
                      <p className="text-xs leading-relaxed text-muted-foreground">{item.desc}</p>
                    </div>
                  </div>
                  {index < workflowSteps.length - 1 && (
                    <ArrowRight className="absolute top-8 -right-3.5 z-10 hidden h-4 w-4 text-primary/30 md:block" />
                  )}
                </div>
              ))}
            </div>
          </ScrollAnimatedSection>
        </div>
      </section>

      <ScrollAnimatedSection>
        <BeforeAfterDemo />
      </ScrollAnimatedSection>

      <ScrollAnimatedSection>
        <UseCaseCards />
      </ScrollAnimatedSection>

      <section id="features" className="relative scroll-mt-24 overflow-hidden py-14 md:py-20">
        <div className="absolute inset-0 bg-card/20" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border/40 to-transparent" />
        <div className="absolute top-[20%] right-[15%] h-[400px] w-[400px] rounded-full bg-primary/[0.04] blur-[140px]" />

        <div className="container relative z-10 px-4 md:px-6">
          <ScrollAnimatedSection>
            <div className="mx-auto mb-10 max-w-2xl text-center">
              <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                <Sparkles className="h-3.5 w-3.5" />
                Product
              </div>
              <h2 className="mb-3 font-sans text-2xl font-bold md:text-3xl">Built around how requirements actually move</h2>
              <p className="text-base text-muted-foreground">
                Reqqy is designed for the messy middle between product thinking and engineering execution.
              </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {featureCards.map((feature, index) => (
                <div
                  key={feature.title}
                  className="group relative rounded-xl border border-border/30 glass p-5 transition-all duration-300 hover:-translate-y-0.5 hover:border-primary/20 md:p-6"
                  style={{ animationDelay: `${index * 0.08}s` }}
                >
                  <div className="absolute inset-0 rounded-xl bg-gradient-to-b from-primary/[0.02] to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                  <div className="relative z-10">
                    <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-lg gradient-primary">
                      <feature.icon className="h-5 w-5 text-primary-foreground" />
                    </div>
                    <h3 className="mb-2 text-base font-semibold">{feature.title}</h3>
                    <p className="text-sm leading-relaxed text-muted-foreground">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </ScrollAnimatedSection>
        </div>
      </section>

      <section className="relative overflow-hidden py-14 md:py-20">
        <div className="absolute inset-0 bg-card/30" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border/40 to-transparent" />
        <div className="absolute top-[10%] left-[20%] h-[400px] w-[500px] rounded-full bg-primary/[0.03] blur-[140px]" />

        <div className="container relative z-10 px-4 md:px-6">
          <ScrollAnimatedSection>
            <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
              <div>
                <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                  <Shield className="h-3.5 w-3.5" />
                  Trust and control
                </div>
                <h2 className="mb-4 font-sans text-2xl font-bold md:text-3xl">Requirements work is sensitive by default</h2>
                <p className="mb-6 text-base leading-relaxed text-muted-foreground">
                  Reqqy is built for product decisions, user workflows, and internal context. The product keeps teams in control by making context visible, preserving versions, and supporting privacy-aware workflows.
                </p>
                <ul className="space-y-2">
                  {securityFeatures.map((feature) => (
                    <li key={feature} className="flex items-center gap-2.5 rounded-md p-1.5 -ml-1.5">
                      <CheckCircle2 className="h-4 w-4 flex-shrink-0 text-reqqy-success" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                  <Button asChild size="sm" className="gradient-primary text-primary-foreground hover:opacity-90">
                    <Link to="/security">
                      Security overview
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                  <Button asChild variant="outline" size="sm" className="border-border/60 hover:border-primary/30">
                    <Link to="/contact-sales">Talk to us</Link>
                  </Button>
                </div>
              </div>
              <div className="relative">
                <div className="relative overflow-hidden rounded-xl border border-border/30 glass p-6">
                  <div className="absolute top-0 left-1/2 h-[150px] w-[250px] -translate-x-1/2 rounded-full bg-primary/[0.05] blur-[60px]" />
                  <div className="relative z-10 flex flex-col items-center justify-center gap-6 py-4">
                    <div className="flex h-16 w-16 items-center justify-center rounded-xl gradient-primary glow-primary">
                      <Lock className="h-8 w-8 text-primary-foreground" />
                    </div>

                    <div className="max-w-xs text-center">
                      <div className="mb-1 font-sans text-2xl font-bold">Context-aware, not black-box</div>
                      <div className="text-sm leading-relaxed text-muted-foreground">
                        Runs can show the product memory, project context, input, and version history behind the output.
                      </div>
                    </div>

                    <div className="grid w-full max-w-sm grid-cols-2 gap-4">
                      <div className="rounded-lg border border-border/30 bg-background/40 px-4 py-3 text-center">
                        <div className="text-lg font-bold">Visible</div>
                        <div className="text-[11px] text-muted-foreground">Context traces</div>
                      </div>
                      <div className="rounded-lg border border-border/30 bg-background/40 px-4 py-3 text-center">
                        <div className="text-lg font-bold">Versioned</div>
                        <div className="text-[11px] text-muted-foreground">Requirement history</div>
                      </div>
                    </div>

                    <div className="flex flex-wrap justify-center gap-2 text-[11px] text-muted-foreground">
                      {["No model training", "Temporary mode", "Export control"].map((badge) => (
                        <span key={badge} className="rounded-md border border-border/30 bg-background/50 px-2.5 py-1 font-medium">
                          {badge}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </ScrollAnimatedSection>
        </div>
      </section>

      <FAQSection />

      <section className="relative overflow-hidden py-20 md:py-28">
        <div className="absolute inset-0 bg-gradient-to-b from-background via-background to-card/50" />
        <div className="absolute top-0 left-1/2 h-[300px] w-[600px] -translate-x-1/2 rounded-full bg-primary/[0.10] blur-[140px]" />
        <div className="absolute inset-0 opacity-[0.02] mix-blend-overlay" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
        }} />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />

        <div className="container relative z-10 px-4 text-center md:px-6">
          <ScrollAnimatedSection>
            <div className="mb-8 inline-flex items-center gap-2 rounded-full border border-primary/20 glass px-3.5 py-1.5 text-xs">
              <Brain className="h-3.5 w-3.5 text-primary" />
              <span className="text-muted-foreground">Requirements Intelligence for Product Teams</span>
            </div>
            <h2 className="mb-4 font-sans text-3xl font-bold leading-[1.1] tracking-[-0.02em] md:text-4xl lg:text-5xl">
              Make the handoff clearer before it gets expensive.
            </h2>
            <p className="mx-auto mb-8 max-w-xl text-base leading-relaxed text-muted-foreground md:text-lg">
              Start with an idea or a PRD draft. Reqqy helps you structure it, review it, refine it, and move it toward delivery.
            </p>
            <div className="flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Button
                asChild
                size="lg"
                className="relative group h-11 px-7 text-sm font-semibold gradient-primary text-primary-foreground overflow-hidden transition-all duration-300 hover:shadow-[0_0_30px_-8px_hsl(var(--primary)/0.5)]"
              >
                <Link to="/auth?mode=signup">
                  <span className="relative z-10 flex items-center gap-2">
                    <Sparkles className="h-4 w-4" />
                    Start free
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                  <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/10 to-transparent transition-transform duration-700 group-hover:translate-x-full" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="h-11 px-7 text-sm border-border/60 hover:border-primary/40">
                <Link to="/contact-sales">Talk to us</Link>
              </Button>
            </div>
            <p className="mt-4 text-xs tracking-wide text-muted-foreground/70">
              No credit card required · Built for rough ideas, PRDs, and engineering handoffs
            </p>
          </ScrollAnimatedSection>
        </div>

        <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-background to-transparent" />
      </section>
    </PublicLayout>
  );
}
